/**
 * COPY STRATEGY — BoltDriverAccountingPage
 *
 * Awareness:      Problem-Aware (Stage 2/5)
 *                 Reader knows they need to handle Bolt documents but doesn't
 *                 know which ones, where to find them, or what they're for.
 *
 * Sophistication: Stage 2 — Enlarged Claim
 *                 Urgency via "PRIVALOTE" (MUST). Simple direct instruction
 *                 with clear actionable steps. No complex mechanism needed.
 *
 * Avatar:         New-to-intermediate driver, first 1–6 months.
 *                 Just started with Bolt, overwhelmed by admin.
 *                 Needs practical how-to, not theory.
 *
 * Hook:           Urgency/fear — "3 dokumentai, kuriuos PRIVALOTE atsisiųsti"
 * Structure:      List documents → Explain each → Where to find → CTA
 * Proof:          Specific document names and exact portal locations
 * Funnel:         Entry point for beginners, leads to taxes and VAT articles
 */
import { motion } from 'framer-motion'
import { PiWarning, PiCheckCircle } from 'react-icons/pi'
import { Link } from 'react-router-dom'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const BoltDriverAccountingPage = () => {
  usePageSetup('Bolt apskaita: 3 dokumentai, kuriuos PRIVALOTE atsisiųsti kas mėnesį', {
    scroll: true,
    description: 'Kuriuos 3 Bolt dokumentus turite atsisiųsti kas mėnesį? Kelionių CSV, komisinių SF ir suvestinė — be jų rizikuojate baudomis ir prarandate išlaidų atskaitymus.',
    type: 'article',
    datePublished: '2025-10-22',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText
          as="h1"
          className="text-zinc-900 text-2xl font-medium mt-3 mb-2"
        >
          Bolt apskaita: 3 dokumentai, kuriuos PRIVALOTE atsisiųsti kas mėnesį
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Apskaita</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">5 min skaitymo</span>
        </MotionDiv>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-2"
        >
          Bolt vairuotojo portalas generuoja tris skirtingus dokumentus. Kiekvienas reikalingas konkrečiam tikslui — pajamų apskaitai, PVM deklaracijai arba išlaidų įrodymui.
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-4"
        >
          Šiame straipsnyje rasite tikslias instrukcijas: kur rasti, kaip atsisiųsti ir kam naudoti kiekvieną dokumentą.
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="p" className="text-blue-900 text-sm">
            <strong>Apie mokesčius:</strong> jei norite suprasti, kokius mokesčius (GPM, PSD, VSD, PVM) turite mokėti ir kaip juos apskaičiuoti — skaitykite{' '}
            <Link to="/blog/pavezejo-mokesciai-gpm-psd-vsd" className="text-blue-700 underline font-medium">
              Mokesčiai pavežėjui 2026
            </Link>.
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          1. Kelionių sąskaitos faktūros (CSV)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Pajamų apskaita — kiek uždirbote
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            CSV failas su kiekviena kelione: data, maršrutas, kaina, komisinis mokestis. Tai pagrindinis dokumentas, iš kurio skaičiuojamos jūsų <strong>bruto pajamos</strong>.
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3 mb-3">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              Ką rasite faile:
            </MotionText>
            <MotionDiv className="space-y-1.5">
              <MotionText as="p" className="text-zinc-700 text-sm">• Kelionės data ir laikas</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Bruto suma (ką sumokėjo keleivis)</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Bolt komisinis mokestis (~25%)</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Neto suma (pervesta jums)</MotionText>
            </MotionDiv>
          </MotionDiv>
          <MotionDiv className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <MotionText as="p" className="text-blue-900 text-xs font-medium mb-1">
              Kaip atsisiųsti:
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs">
              📍 partners.bolt.eu → Meniu → Kelionių SF → Pasirinkite mėnesį → Atsisiųsti CSV
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          2. Komisinio mokesčio sąskaita faktūra (PDF)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            PVM deklaracija — Atvirkštinis PVM
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            Oficiali sąskaita faktūra iš Bolt Operations OÜ (Estija). Šiame dokumente nurodyta komisinio mokesčio suma, nuo kurios privalote <strong>deklaruoti ir sumokėti 21% Atvirkštinį PVM</strong>.
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3 mb-3">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              Ką rasite faile:
            </MotionText>
            <MotionDiv className="space-y-1.5">
              <MotionText as="p" className="text-zinc-700 text-sm">• Bolt Operations OÜ rekvizitai ir PVM kodas</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Komisinio mokesčio suma (be PVM)</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Laikotarpis (mėnuo)</MotionText>
            </MotionDiv>
          </MotionDiv>
          <MotionDiv className="bg-amber-50 border border-amber-200 p-3 rounded-lg mb-3">
            <div className="flex gap-2">
              <PiWarning className="min-w-4 h-4 text-amber-700 mt-0.5" />
              <MotionText as="p" className="text-amber-800 text-xs">
                <strong>Svarbu:</strong> šį dokumentą PRIVALOTE turėti kiekvieną mėnesį. Be jo negalėsite teisingai deklaruoti PVM ir įtraukti komisinio į išlaidas.
              </MotionText>
            </div>
          </MotionDiv>
          <MotionDiv className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <MotionText as="p" className="text-blue-900 text-xs font-medium mb-1">
              Kaip atsisiųsti:
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs">
              📍 partners.bolt.eu → Meniu → Sąskaitos faktūros → Pasirinkite mėnesį → Atsisiųsti PDF
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-zinc-50 border-l-4 border-zinc-700 rounded-r-xl mb-4">
          <MotionText as="p" className="text-zinc-700 text-sm">
            <strong>Pavyzdys:</strong> jei Bolt komisinis per mėnesį buvo 500 €, Atvirkštinis PVM = 500 × 21% = <strong>105 €</strong>. Šią sumą mokate VMI iki kito mėnesio 25 d.
          </MotionText>
          <MotionText as="p" className="text-zinc-600 text-xs mt-2">
            Detalų PVM gidą rasite straipsnyje{' '}
            <Link to="/blog/atvirkstinis-pvm-pavezejams" className="text-blue-600 underline">
              Atvirkštinis PVM: mokestis, kurį dauguma pavežėjų pamiršta
            </Link>.
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          3. Mėnesinė suvestinė (PDF)
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Papildomos pajamos — premijos, arbatpinigiai, km
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            Suvestinė apima tai, ko nėra kelionių CSV: <strong>Bolt premijas, arbatpinigius ir nuvažiuotus kilometrus</strong>. Be šio dokumento jūsų pajamų apskaita bus nepilna.
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3 mb-3">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              Ką rasite faile:
            </MotionText>
            <MotionDiv className="space-y-1.5">
              <MotionText as="p" className="text-zinc-700 text-sm">• Premijos (garantijos, referalai, akcijos)</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Arbatpinigiai (visi per mėnesį)</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Nuvažiuoti kilometrai (naudinga kuro apskaitai)</MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm">• Kelionių skaičius</MotionText>
            </MotionDiv>
          </MotionDiv>
          <MotionDiv className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <MotionText as="p" className="text-blue-900 text-xs font-medium mb-1">
              Kaip atsisiųsti:
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs">
              📍 partners.bolt.eu → Meniu → Mėnesinės suvestinės → Pasirinkite mėnesį → Atsisiųsti PDF
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Mėnesinis darbo srautas
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-4"
        >
          Štai kaip atrodo teisingas Bolt vairuotojo apskaitos procesas kiekvieną mėnesį:
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-5 space-y-5">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Atsisiųskite 3 dokumentus iš partners.bolt.eu
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Kelionių CSV + Komisinių SF + Mėnesinę suvestinę
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Įkelkite į Finhudas
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Sistema automatiškai nuskaito pajamas, keliones, premijas ir apskaičiuoja Atvirkštinį PVM
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Deklaruokite ir sumokėkite PVM iki 25 d.
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Finhudas parodo tikslią sumą — jums belieka pervesti į VMI
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  4
                </div>
              </div>
              <div>
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Sumokėkite PSD iki mėnesio pabaigos
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  80,48 € (2026 m., 6,98% nuo 1 153 € MMA) — Finhudas primena terminą
                </MotionText>
              </div>
            </div>
          </div>

          <div className="mx-5 border-t border-zinc-250" />

          <div className="p-5">
            <div className="flex gap-3">
              <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <MotionText as="p" className="text-green-700 text-sm font-medium mb-1">
                  Viskas užtrunka 3 minutes
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Atsisiųsti 3 failus + įkelti į Finhudas = apskaita atlikta. Sistema viską suskaičiuoja automatiškai — pajamas, komisinius, PVM ir mokesčius.
                </MotionText>
              </div>
            </div>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Dažniausios klaidos
        </MotionText>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Ko nedaryti
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  ❌ Naudoti tik banko išrašą vietoj Bolt dokumentų — ten matosi tik pervedimas, ne bruto pajamos
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Pamiršti komisinio SF — be jos negalėsite deklaruoti Atvirkštinio PVM ir prarasite išlaidų atskaitymą
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Ignoruoti mėnesinę suvestinę — premijos ir arbatpinigiai irgi yra apmokestinamos pajamos
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Atsisiųsti dokumentus tik metų pabaigoje — Bolt portalas saugo tik ribotą laikotarpį
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <FinhudasCTA awareness="solution" />

        <RelatedArticles articles={[
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' },
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM: kaip deklaruoti Bolt komisinius' },
          { slug: 'mokesciu-kalendorius-kada-moketi-mokescius', title: 'Mokesčių kalendorius 2026: visi terminai' }
        ]} />

        <motion.div
          className="mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ButtonLink variant="secondary" to="/blog">
            Grįžti į straipsnius
          </ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default BoltDriverAccountingPage
